import '@hotwired/turbo';
import 'controllers';
import ThirdParty from 'lib/third_party';

document.addEventListener('turbo:before-fetch-request', (event) => {
  const { fetchOptions } = event.detail;

  if (
    fetchOptions.headers &&
    fetchOptions.headers.Accept &&
    fetchOptions.headers.Accept.includes('text/vnd.turbo-stream.html')
  ) {
    fetchOptions.headers.Accept = 'text/html, application/xhtml+xml';
  }
});

// Reinitialize the Judge.me review widget after Turbo navigation.
// Only the review_widget module is reloaded: re-running loader.js as well
// marks the placeholder as done without rendering it and races the module,
// which intermittently leaves the widget unstyled.
ThirdParty.onTurboNavigation(() => {
  if (typeof jdgm === 'undefined') return;
  if (!document.querySelector('.jdgm-review-widget')) return;

  const loaderScript = document.querySelector('script[src*="judgeme"][src*="loader.js"]');
  if (loaderScript)
    ThirdParty.reloadModule(loaderScript.src.replace('loader.js', 'review_widget.js'));
});

// Cache a pristine Judge.me placeholder so restored pages re-render cleanly
// instead of showing a dead, half-hydrated widget.
document.addEventListener('turbo:before-cache', () => {
  document.querySelectorAll('.jdgm-review-widget').forEach((widget) => {
    widget.innerHTML = '';
    widget.className = 'jdgm-widget jdgm-review-widget';
  });
});

// Remount the Hive Delivery Promise widget after Turbo swaps the DOM.
// The widget bundle lives in <head> and mounts once on load, so a Turbo visit,
// a morph refresh or a variant frame navigation leaves the page without it.
// start() is exported by the bundle and remounts it.
//
// Mounting is asynchronous: start() looks for its anchor element and retries
// ten times every 500ms before inserting the widget, and it reports nothing
// back. Checking the DOM is therefore not enough, because a morph fires both
// turbo:morph and turbo:load and a frame load is followed by turbo:load, so a
// second call would land while the first is still polling and both would
// insert. Remember the pending mount instead, tied to the body it targets so a
// Turbo visit that replaces the body can mount right away, and expiring with
// the polling window so a mount that never found its anchor can be retried.
const MOUNT_POLL_WINDOW = 5000;

// The cart drawer is a snippet in the header group, so it takes no app block
// and the provider is pointed at this anchor instead. See cart-drawer.liquid.
const CART_DRAWER_ANCHOR = '.cart-drawer-delivery-promise';

// The app embed renders an inline script in <head> that defines this hook,
// carrying the config for the page being viewed: the variant id on a product,
// the line items on the cart. The bundle calls it once, while it initialises,
// and never again: start() does not. Hive's own product path then polls
// location.href and only picks up a new variant from a ?variant= query param,
// which a Turbo visit to another product does not have. So the config has to be
// re-applied by hand, or the widget is rebuilt from the previous page's data.
const HOOK = 'onDeliveryPromiseWidgetLoaded';

// Re-applying it is only correct if the hook belongs to the page on screen, and
// that is not a given. Turbo merges heads by appending the scripts the incoming
// head has and the current one does not, so a page visited a second time
// contributes nothing: its script is still in the head from the first visit, so
// it is not appended, it does not run again, and the hook keeps whatever the
// last newly seen page defined. Re-applying it would then write that page's
// config onto this one, and since the cart defines the hook too, a product page
// can end up rebuilt as a cart.
//
// So the head is kept down to a single script, the one for the page being
// rendered. The merge runs before turbo:before-render and appends to the end,
// so the last of them is always the incoming page's, and dropping the ones
// before it means the next merge has nothing of that page left to match and
// appends and runs its script again.
//
// The surviving one has to stay, rather than clearing the head outright: Turbo
// caches this page's snapshot when the shopper leaves it, head included, and
// restores that snapshot on a back or forward instead of refetching. A snapshot
// cached without the script has nothing for the merge to run, which would leave
// a restored page rebuilt from the config of wherever the shopper came from.
const dropStaleConfigHooks = () => {
  const hooks = [...document.head.querySelectorAll('script:not([src])')].filter((script) =>
    script.textContent.includes(HOOK)
  );

  hooks.slice(0, -1).forEach((script) => script.remove());
};

document.addEventListener('turbo:before-render', dropStaleConfigHooks);

// The provider has no server side selector for the drawer, and the drawer is a
// snippet in the header group so it cannot carry an app block to supply one.
// Set it from here instead, and only while it is empty, so a selector they
// configure on their side later wins over ours without a code change.
const pointConfigsAtDrawer = (hive) => {
  [hive.productConfigs, hive.cartConfigs].forEach((configs) => {
    if (configs && !configs.dynamicQuerySelector) {
      configs.dynamicQuerySelector = CART_DRAWER_ANCHOR;
    }
  });
};

// Wrap the hook rather than assigning the selector after the fact. The bundle
// calls the hook while it initialises and mounts immediately afterwards, so
// this is the last moment the drawer can still be configured in time for that
// first mount, the one nothing of ours triggers. Remounts go through the hook
// too, and each Turbo render re-runs the inline script that defines it, which
// drops the wrapper, so it is reapplied before every call.
const pointHookAtDrawer = () => {
  const hook = window[HOOK];
  if (typeof hook !== 'function' || hook.pointsAtDrawer) return;

  const wrapped = (hive) => {
    hook(hive);
    pointConfigsAtDrawer(hive);
  };

  wrapped.pointsAtDrawer = true;
  window[HOOK] = wrapped;
};

pointHookAtDrawer();

// Only the keys the inline script sets are touched: the provider's configs
// setter skips undefined keys and merges productConfigs and cartConfigs with
// Object.assign, so querySelector and dynamicQuerySelector, which the cart
// drawer mount depends on, are left alone.
const applyCurrentPageConfig = () => {
  pointHookAtDrawer();
  window[HOOK]?.(window.DeliveryPromiseWidget.HiveDeliveryPromise);
};

let pendingMount = null;

// The drawer widget is mounted by a MutationObserver on the body, and start()
// only registers that observer, it never scans the DOM itself. Without this the
// drawer stays empty until something unrelated happens to mutate the page, so
// whether the widget shows up depends on what the other apps are doing. The
// observer only reacts to a record whose target contains the anchor, so the
// attribute goes on the parent: marking the anchor itself would not be seen.
const nudgeDrawerObserver = () => {
  document.querySelectorAll(CART_DRAWER_ANCHOR).forEach((anchor) => {
    anchor.parentElement?.setAttribute('data-delivery-promise-nudge', String(Date.now()));
  });
};

const restartDeliveryPromise = () => {
  if (!window.DeliveryPromiseWidget) return;
  if (document.querySelector('delivery-promise-widget')) return;

  const isMountPending =
    pendingMount !== null &&
    pendingMount.body === document.body &&
    Date.now() - pendingMount.startedAt < MOUNT_POLL_WINDOW;

  if (isMountPending) return;

  pendingMount = { body: document.body, startedAt: Date.now() };
  applyCurrentPageConfig();
  window.DeliveryPromiseWidget.start();
  nudgeDrawerObserver();
};

ThirdParty.onTurboNavigation(restartDeliveryPromise);
document.addEventListener('turbo:morph', restartDeliveryPromise);

// The variant selector is a turbo-frame, and Hive follows ?variant= by itself,
// but only once mounted: on a sold out first variant it never mounts at all.
// Remount when the frame swaps so picking an available variant brings it back.
document.addEventListener('turbo:frame-load', (event) => {
  if (event.target.id.startsWith('product-')) restartDeliveryPromise();
});

// The widget is injected by JS and absent from the server HTML, so keep it out
// of the snapshot: a restored page would otherwise show a dead element holding
// the previous product's variant.
document.addEventListener('turbo:before-cache', () => {
  document.querySelectorAll('delivery-promise-widget').forEach((widget) => widget.remove());
});
